'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import ProcessStep from './ProcessStep';
import ComparisonTable from './ComparisonTable';
import PricingCalculator from './PricingCalculator';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  features: Array<{
    icon: string;
    text: string;
  }>;
  pricing: {
    aed: string;
    usd: string;
  };
}

interface ProcessStepData {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ComparisonFeature {
  name: string;
  basic: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

const ServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  { id: 'all', name: 'All Services', icon: 'Squares2X2Icon' },
  { id: 'marketing', name: 'Marketing', icon: 'MegaphoneIcon' },
  { id: 'design', name: 'Design', icon: 'SwatchIcon' },
  { id: 'production', name: 'Productions', icon: 'VideoCameraIcon' },
  { id: 'digital', name: 'Digital & Web', icon: 'ComputerDesktopIcon' },
  { id: 'pr', name: 'PR', icon: 'MegaphoneIcon' }];


  const services: Service[] = [
  // 1. STRATEGY DEVELOPMENT
  {
    id: 'marketing-strategy',
    title: 'Marketing Strategy',
    description:
    'Data-driven marketing strategies tailored to your market. We analyze competitors, define positioning, and build a roadmap that turns your marketing budget into measurable business growth.',
    image: "/assets/images/stock/photo-1542744095-fcf48d80b0fd.webp",
    imageAlt: 'Business team collaborating around table with laptops discussing marketing strategy',
    features: [
    { icon: 'CheckCircleIcon', text: 'Market Research & Competitor Analysis' },
    { icon: 'CheckCircleIcon', text: 'Positioning & Messaging Framework' },
    { icon: 'CheckCircleIcon', text: 'Campaign Planning & Roadmap' },
    { icon: 'CheckCircleIcon', text: 'KPI Setting & Performance Tracking' }],
    pricing: { aed: '', usd: '' }
  },
  // MARKETING
  {
    id: 'social-media-management',
    title: 'Social Media Management',
    description:
    'Full monthly management of your presence on Instagram, LinkedIn, Facebook, TikTok, and YouTube. We handle content creation, scheduling, community engagement, and growth – so your brand stays active and consistent every day.',
    image: "/assets/images/stock/photo-1611926653458-09294b3142bf.webp",
    imageAlt: 'Social media manager working with multiple platform dashboards',
    features: [
    { icon: 'CheckCircleIcon', text: 'Instagram, LinkedIn, Facebook, TikTok, YouTube' },
    { icon: 'CheckCircleIcon', text: 'Content Creation & Scheduling' },
    { icon: 'CheckCircleIcon', text: 'Community Management & DMs' },
    { icon: 'CheckCircleIcon', text: 'Monthly Reports & Growth Tracking' }],
    pricing: { aed: '', usd: '' }
  },
  // 4. DESIGN
  {
    id: 'graphic-design',
    title: 'Graphic Design',
    description:
    'Professional graphic design for every touchpoint – social media visuals, presentations, marketing collateral, and print materials. Clean, on-brand designs that make your business look as good as it performs.',
    image: "/assets/images/stock/photo-1626785774573-4b799315345d.webp",
    imageAlt: 'Graphic designer working on brand identity with color swatches and layouts',
    features: [
    { icon: 'CheckCircleIcon', text: 'Social Media Visuals & Templates' },
    { icon: 'CheckCircleIcon', text: 'Presentation & Pitch Deck Design' },
    { icon: 'CheckCircleIcon', text: 'Marketing Collateral & Print' },
    { icon: 'CheckCircleIcon', text: 'Packaging & Merchandise Design' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'motion-design',
    title: 'Motion Design',
    description:
    'Dynamic motion graphics and animations that bring your brand to life. Logo reveals, animated infographics, social media animations, and video intros that capture attention and communicate complex ideas simply.',
    image: "/assets/images/stock/photo-1550745165-9bc0b252726f.webp",
    imageAlt: 'Designer creating motion graphics and animations on computer with graphics tablet',
    features: [
    { icon: 'CheckCircleIcon', text: 'Logo Animations & Intros' },
    { icon: 'CheckCircleIcon', text: 'Animated Infographics & Explainers' },
    { icon: 'CheckCircleIcon', text: 'Social Media Animations' },
    { icon: 'CheckCircleIcon', text: '2D & 3D Motion Graphics' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'branding',
    title: 'Branding',
    description:
    'Complete brand identity development from scratch or a strategic rebrand. We craft logos, visual systems, brand guidelines, and all the assets you need to present a cohesive, professional image across every channel.',
    image: "/assets/images/stock/photo-1558655146-9f40138edfeb.webp",
    imageAlt: 'Brand identity materials including logo, business cards, and style guide spread on desk',
    features: [
    { icon: 'CheckCircleIcon', text: 'Logo Design & Visual Identity' },
    { icon: 'CheckCircleIcon', text: 'Brand Guidelines & Style Guide' },
    { icon: 'CheckCircleIcon', text: 'Typography & Color Systems' },
    { icon: 'CheckCircleIcon', text: 'Brand Collateral & Templates' }],
    pricing: { aed: '', usd: '' }
  },
  // 5. PERFORMANCE
  {
    id: 'targeted-advertising',
    title: 'Targeted Advertising',
    description:
    'Precision-targeted ad campaigns on Meta, TikTok, LinkedIn, and other platforms. We handle audience segmentation, creative production, A/B testing, and continuous optimization to maximize your return on ad spend.',
    image: "/assets/images/stock/photo-1551288049-bebda4e38f71.webp",
    imageAlt: 'Digital advertising dashboard showing campaign metrics and audience targeting',
    features: [
    { icon: 'CheckCircleIcon', text: 'Meta, TikTok & LinkedIn Ads' },
    { icon: 'CheckCircleIcon', text: 'Audience Segmentation & Targeting' },
    { icon: 'CheckCircleIcon', text: 'A/B Testing & Creative Optimization' },
    { icon: 'CheckCircleIcon', text: 'ROAS Tracking & Reporting' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'contextual-advertising',
    title: 'Contextual Advertising',
    description:
    'Google Ads and search engine marketing that puts your business in front of people actively looking for your services. Keyword research, ad copywriting, bid management, and landing page optimization for maximum conversions.',
    image: "/assets/images/stock/photo-1432888622747-4eb9a8efeb07.webp",
    imageAlt: 'Search engine marketing dashboard with keyword performance and bid management',
    features: [
    { icon: 'CheckCircleIcon', text: 'Google Ads & Search Campaigns' },
    { icon: 'CheckCircleIcon', text: 'Keyword Research & Strategy' },
    { icon: 'CheckCircleIcon', text: 'Ad Copywriting & Extensions' },
    { icon: 'CheckCircleIcon', text: 'Conversion Tracking & Optimization' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'funnel-development',
    title: 'Funnel Development & Optimization',
    description:
    'End-to-end sales funnel design that guides prospects from awareness to purchase. We map the customer journey, build landing pages, set up email sequences, and optimize every step for higher conversion rates.',
    image: "/assets/images/stock/photo-1533750349088-cd871a92f312.webp",
    imageAlt: 'Marketing funnel visualization with conversion metrics on whiteboard',
    features: [
    { icon: 'CheckCircleIcon', text: 'Customer Journey Mapping' },
    { icon: 'CheckCircleIcon', text: 'Landing Page Design & A/B Testing' },
    { icon: 'CheckCircleIcon', text: 'Email Sequence Automation' },
    { icon: 'CheckCircleIcon', text: 'Conversion Rate Optimization' }],
    pricing: { aed: '', usd: '' }
  },
  // 6. PRODUCTIONS
  {
    id: 'full-cycle-production',
    title: 'Full-Cycle Production',
    description:
    'Complete production support from concept to final delivery. We manage pre-production planning, crew and equipment, on-set direction, and post-production – a single team handling every stage of your project.',
    image: "/assets/images/stock/photo-1579165466741-7f35e4755660.webp",
    imageAlt: 'Professional film crew on set with camera equipment and lighting',
    features: [
    { icon: 'CheckCircleIcon', text: 'Pre-Production & Storyboarding' },
    { icon: 'CheckCircleIcon', text: 'Professional Crew & Equipment' },
    { icon: 'CheckCircleIcon', text: 'On-Set Direction & Management' },
    { icon: 'CheckCircleIcon', text: 'Post-Production & Delivery' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'photo-shoots',
    title: 'Photo Shoots',
    description:
    'Professional photography for products, portraits, events, and lifestyle content. Studio and on-location shoots with expert lighting, styling direction, and full retouching – images that elevate your brand.',
    image: "/assets/images/stock/photo-1516035069371-29a1b244cc32.webp",
    imageAlt: 'Professional photographer shooting in studio with lighting equipment',
    features: [
    { icon: 'CheckCircleIcon', text: 'Product & Lifestyle Photography' },
    { icon: 'CheckCircleIcon', text: 'Studio & On-Location Shoots' },
    { icon: 'CheckCircleIcon', text: 'Professional Retouching' },
    { icon: 'CheckCircleIcon', text: 'Multi-Platform Image Formats' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'video-shoots',
    title: 'Video Shoots',
    description:
    'High-quality video production for commercials, corporate films, social media content, and event recaps. Multi-camera setups, drone footage, and cinematic techniques that make your story unforgettable.',
    image: "/assets/images/stock/photo-1662988839625-af2af0536ab7.webp",
    imageAlt: 'Film production crew setting up professional camera equipment for video shoot',
    features: [
    { icon: 'CheckCircleIcon', text: '4K & Cinema-Grade Shooting' },
    { icon: 'CheckCircleIcon', text: 'Multi-Camera & Drone Footage' },
    { icon: 'CheckCircleIcon', text: 'Color Grading & Sound Design' },
    { icon: 'CheckCircleIcon', text: 'Social & Broadcast Formats' }],
    pricing: { aed: '', usd: '' }
  },
  // 7. PR
  {
    id: 'influencer-marketing',
    title: 'KOL & Influencer Hostings',
    description:
    'Strategic influencer partnerships that put your brand in front of the right audiences. We handle talent scouting, negotiation, campaign briefs, content approval, and performance tracking across all platforms.',
    image: "/assets/images/stock/photo-1611162616305-c69b3fa7fbe0.webp",
    imageAlt: 'Content creator filming brand collaboration with professional setup',
    features: [
    { icon: 'CheckCircleIcon', text: 'Influencer Scouting & Vetting' },
    { icon: 'CheckCircleIcon', text: 'Campaign Briefs & Negotiations' },
    { icon: 'CheckCircleIcon', text: 'Content Review & Approval' },
    { icon: 'CheckCircleIcon', text: 'Performance Tracking & ROI' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'seeding',
    title: 'Seeding & Reputation Management',
    description:
    'Product seeding campaigns that generate authentic buzz, combined with ongoing reputation management. We identify the right creators, coordinate delivery, track organic mentions, and protect your brand perception across platforms.',
    image: "/assets/images/stock/photo-1556742049-0cfed4f6a45d.webp",
    imageAlt: 'Branded product packages prepared for influencer seeding campaign',
    features: [
    { icon: 'CheckCircleIcon', text: 'Creator & Media Identification' },
    { icon: 'CheckCircleIcon', text: 'Product Packaging & Delivery' },
    { icon: 'CheckCircleIcon', text: 'Organic Mention Tracking' },
    { icon: 'CheckCircleIcon', text: 'UGC Collection & Repurposing' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'event-support',
    title: 'Event Support',
    description:
    'End-to-end event coverage and support – from pre-event promotion to on-site content capture and post-event recaps. Photo, video, live social coverage, and same-day highlights for conferences, launches, and exhibitions.',
    image: "/assets/images/stock/photo-1625638877505-b7a3fc705b67.webp",
    imageAlt: 'Corporate event with professional media coverage team and stage lighting',
    features: [
    { icon: 'CheckCircleIcon', text: 'Pre-Event Promotion & Teasers' },
    { icon: 'CheckCircleIcon', text: 'On-Site Photo & Video Coverage' },
    { icon: 'CheckCircleIcon', text: 'Live Social Media Coverage' },
    { icon: 'CheckCircleIcon', text: 'Post-Event Recap & Highlights' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'media-outreach',
    title: 'Media Relations',
    description:
    'Online, social and print coverage in top-tier titles in the UAE and worldwide. We secure strategic placements that build authority and public visibility for your brand.',
    image: "/assets/images/stock/photo-1557804506-669a67965ba0.webp",
    imageAlt: 'Press conference setup with microphones and media professionals',
    features: [
    { icon: 'CheckCircleIcon', text: 'Press Release Writing & Distribution' },
    { icon: 'CheckCircleIcon', text: 'Journalist & Editor Outreach' },
    { icon: 'CheckCircleIcon', text: 'Interview & Feature Placement' },
    { icon: 'CheckCircleIcon', text: 'Media Monitoring & Clipping' }],
    pricing: { aed: '', usd: '' }
  },
  // DIGITAL & WEB
  {
    id: 'landing-pages',
    title: 'Landing Page Development',
    description:
    'High-converting landing pages designed to turn visitors into leads and customers. We handle strategy, design, development, and optimization – pages that look great and perform even better.',
    image: "/assets/images/stock/photo-1547658719-da2b51169166.webp",
    imageAlt: 'Web developer designing landing page on large monitor with code editor',
    features: [
    { icon: 'CheckCircleIcon', text: 'Conversion-Focused Design' },
    { icon: 'CheckCircleIcon', text: 'Responsive Development' },
    { icon: 'CheckCircleIcon', text: 'A/B Testing & Optimization' },
    { icon: 'CheckCircleIcon', text: 'Analytics & Tracking Setup' }],
    pricing: { aed: '', usd: '' }
  },
  {
    id: 'chatbot-creation',
    title: 'Chatbot Creation',
    description:
    'Custom chatbots for WhatsApp, Instagram, Telegram, and your website. We build automated conversation flows that qualify leads, answer FAQs, book appointments, and support customers 24/7.',
    image: "/assets/images/stock/photo-1587560699334-cc4ff634909a.webp",
    imageAlt: 'Chat interface showing automated chatbot conversation on mobile device',
    features: [
    { icon: 'CheckCircleIcon', text: 'WhatsApp, Instagram & Telegram Bots' },
    { icon: 'CheckCircleIcon', text: 'Lead Qualification Flows' },
    { icon: 'CheckCircleIcon', text: 'FAQ & Support Automation' },
    { icon: 'CheckCircleIcon', text: 'CRM Integration & Analytics' }],
    pricing: { aed: '', usd: '' }
  }];


  const processSteps: ProcessStepData[] = [
  {
    number: '01',
    title: 'Discovery & Consultation',
    description:
    'We begin with an in-depth consultation to understand your goals, target audience, and project requirements. Our team analyzes your needs and develops a strategic approach tailored to your objectives.',
    icon: 'ChatBubbleLeftRightIcon'
  },
  {
    number: '02',
    title: 'Planning & Strategy',
    description:
    'Our experts create a comprehensive project plan including timelines, resource allocation, and creative direction. We develop detailed storyboards, scripts, and production schedules to ensure smooth execution.',
    icon: 'DocumentTextIcon'
  },
  {
    number: '03',
    title: 'Production & Execution',
    description:
    'Our professional crew brings your vision to life using state-of-the-art equipment and proven techniques. We manage every aspect of production with meticulous attention to detail and quality control.',
    icon: 'VideoCameraIcon'
  },
  {
    number: '04',
    title: 'Post-Production & Refinement',
    description:
    'Our post-production team transforms raw footage into polished content through expert editing, color grading, sound design, and motion graphics. We incorporate your feedback to ensure perfect results.',
    icon: 'SparklesIcon'
  },
  {
    number: '05',
    title: 'Delivery & Support',
    description:
    'We deliver your final content in all required formats optimized for your target platforms. Our team provides ongoing support and can assist with distribution, marketing, and performance tracking.',
    icon: 'CheckBadgeIcon'
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  {
    name: 'Project Consultation',
    basic: true,
    professional: true,
    enterprise: true
  },
  {
    name: 'Professional Equipment',
    basic: 'Standard',
    professional: 'Premium',
    enterprise: 'Cinema Grade'
  },
  {
    name: 'Crew Size',
    basic: '2-3 People',
    professional: '4-6 People',
    enterprise: '8+ People'
  },
  {
    name: 'Shooting Days',
    basic: '1 Day',
    professional: '2-3 Days',
    enterprise: 'Unlimited'
  },
  {
    name: 'Video Duration',
    basic: 'Up to 2 min',
    professional: 'Up to 5 min',
    enterprise: 'Unlimited'
  },
  {
    name: 'Revisions',
    basic: '2 Rounds',
    professional: '4 Rounds',
    enterprise: 'Unlimited'
  },
  {
    name: 'Color Grading',
    basic: 'Basic',
    professional: 'Advanced',
    enterprise: 'Cinema Grade'
  },
  {
    name: 'Motion Graphics',
    basic: false,
    professional: 'Basic',
    enterprise: 'Advanced'
  },
  {
    name: 'Sound Design',
    basic: 'Basic',
    professional: 'Professional',
    enterprise: 'Premium'
  },
  {
    name: 'Drone Footage',
    basic: false,
    professional: true,
    enterprise: true
  },
  {
    name: 'Multi-Camera Setup',
    basic: false,
    professional: true,
    enterprise: true
  },
  {
    name: 'Dedicated Project Manager',
    basic: false,
    professional: false,
    enterprise: true
  },
  {
    name: 'Rush Delivery',
    basic: false,
    professional: true,
    enterprise: true
  },
  {
    name: 'Content Strategy',
    basic: false,
    professional: false,
    enterprise: true
  }];


  const calculatorServices = [
  { id: 'video-basic', name: 'Basic Video Production', basePrice: 15000 },
  {
    id: 'video-premium',
    name: 'Premium Video Production',
    basePrice: 25000
  },
  { id: 'podcast-rental', name: 'Podcast Studio Rental', basePrice: 500 },
  {
    id: 'podcast-full',
    name: 'Full Podcast Production',
    basePrice: 3500
  },
  { id: 'marketing', name: 'Marketing Strategy', basePrice: 8000 },
  { id: 'content', name: 'Content Creation', basePrice: 5000 }];


  const calculatorAddOns = [
  { id: 'drone', name: 'Drone Footage', price: 2000 },
  { id: 'motion', name: 'Motion Graphics', price: 3000 },
  { id: 'rush', name: 'Rush Delivery (48hrs)', price: 5000 },
  { id: 'extra-crew', name: 'Additional Crew Members', price: 1500 },
  { id: 'location', name: 'Location Scouting', price: 1000 },
  { id: 'talent', name: 'Professional Talent', price: 4000 }];


  const categoryMap: Record<string, string[]> = {
    marketing: ['marketing-strategy', 'social-media-management', 'targeted-advertising', 'contextual-advertising', 'funnel-development', 'full-cycle-production'],
    design: ['graphic-design', 'motion-design', 'branding'],
    production: ['full-cycle-production', 'photo-shoots', 'video-shoots', 'event-support'],
    digital: ['landing-pages', 'chatbot-creation'],
    pr: ['media-outreach', 'influencer-marketing', 'seeding'],
  };

  const filteredServices =
  activeCategory === 'all' ?
  services :
  services.filter((service) => (categoryMap[activeCategory] || []).includes(service.id));

  const handleLearnMore = () => {};

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:473:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="473" data-component-end-line="482" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:474:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="474" data-component-end-line="481" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%20p-8%22%7D" className="animate-pulse space-y-8 p-8">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:475:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="475" data-component-end-line="475" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl"></div>
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:476:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="476" data-component-end-line="480" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:478:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="478" data-component-end-line="478" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl"></div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:487:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="487" data-component-end-line="571" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-%5B%230a0a0a%5D%22%7D" className="min-h-screen bg-[#0a0a0a]">
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:488:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="488" data-component-end-line="523" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20py-10%20px-6%20lg%3Apx-8%20overflow-hidden%20bg-%5B%230a0a0a%5D%22%7D" data-page-hero className="relative py-10 px-6 lg:px-8 overflow-hidden bg-[#0a0a0a]">

        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:490:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="490" data-component-end-line="522" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20relative%20z-10%22%7D" className="max-w-7xl mx-auto relative z-10">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:491:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="491" data-component-end-line="504" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" data-reveal className="text-center mb-12">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:492:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="492" data-component-end-line="497" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20space-x-2%20px-4%20py-2%20bg-primary%2F10%20rounded-full%20mb-6%22%7D" className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Icon data-component-id="src/app/services/components/ServicesInteractive.tsx:493:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="493" data-component-end-line="493" data-component-file="ServicesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22SparklesIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="SparklesIcon" size={20} className="text-primary" />
              <span data-component-id="src/app/services/components/ServicesInteractive.tsx:494:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="494" data-component-end-line="496" data-component-file="ServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-outfit%20font-medium%20text-primary%22%2C%22textContent%22%3A%22Where%20Vision%20Meets%20Execution%22%7D" className="text-sm font-outfit font-medium text-primary">
                Where Vision Meets Execution
              </span>
            </div>
            <h1 data-component-id="src/app/services/components/ServicesInteractive.tsx:498:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="498" data-component-end-line="500" data-component-file="ServicesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-5xl%20lg%3Atext-6xl%20font-outfit%20font-bold%20text-white%20mb-6%22%2C%22textContent%22%3A%22Our%20Services%22%7D" className="text-5xl lg:text-6xl font-outfit font-bold text-white mb-6">
              Our Services
            </h1>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:501:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="501" data-component-end-line="503" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%20max-w-3xl%20mx-auto%20leading-relaxed%22%2C%22textContent%22%3A%22We%20help%20ambitious%20brands%20in%20the%20UAE%20and%20beyond%20turn%20attention%20into%20measurable%20growth.%20By%20combining%20cinematic%20video%20production%20and%20marketing%20strategy%2C%20we%20design%20campaigns%20that%20strengthen%20positioning%20and%20accelerate%20demand.%22%7D" className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
              We help ambitious brands in the UAE and beyond turn attention into measurable growth. By combining cinematic video production and marketing strategy, we design campaigns that strengthen positioning and accelerate demand.
            </p>
          </div>

          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:506:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="506" data-component-end-line="521" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-3%20mb-12%22%7D" className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) =>
            <button data-component-id="src/app/services/components/ServicesInteractive.tsx:508:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="508" data-component-end-line="519" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-outfit font-medium transition-all duration-300 ${
              activeCategory === category.id ?
              'bg-primary text-primary-foreground shadow-lg shadow-primary/30' :
              'bg-card text-foreground border border-border hover:border-primary'}`
              }>

                <Icon data-component-id="src/app/services/components/ServicesInteractive.tsx:517:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="517" data-component-end-line="517" data-component-file="ServicesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={20} />
                <span data-component-id="src/app/services/components/ServicesInteractive.tsx:518:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="518" data-component-end-line="518" data-component-file="ServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{category.name}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:525:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="525" data-component-end-line="542" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-8%20px-6%20lg%3Apx-8%22%7D" className="py-8 px-6 lg:px-8">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:526:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="526" data-component-end-line="541" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:527:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="527" data-component-end-line="540" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" data-reveal-group className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:529:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="529" data-component-end-line="537" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D"
              key={service.id}
              title={service.title}
              description={service.description}
              image={service.image}
              imageAlt={service.imageAlt}
              features={service.features}
              pricing={service.pricing}
              onLearnMore={() => handleLearnMore()} />

            )}
          </div>
        </div>
      </section>

      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:544:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="544" data-component-end-line="569" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-10%20px-6%20lg%3Apx-8%20bg-card%22%7D" className="py-10 px-6 lg:px-8 bg-card">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:545:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="545" data-component-end-line="568" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%22%7D" className="max-w-5xl mx-auto">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:546:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="546" data-component-end-line="554" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" data-reveal className="text-center mb-16">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:547:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="547" data-component-end-line="549" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20font-outfit%20font-bold%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Our%20Process%22%7D" className="text-4xl font-outfit font-bold text-primary mb-4">
              Our Process
            </h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:550:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="550" data-component-end-line="553" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%22%2C%22textContent%22%3A%22From%20concept%20to%20delivery%2C%20we%20follow%20a%20proven%20workflow%20that%20ensures%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20exceptional%20results%22%7D" className="text-text-secondary text-lg">
              From concept to delivery, we follow a proven workflow that ensures
              exceptional results
            </p>
          </div>

          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:556:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="556" data-component-end-line="567" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            {processSteps.map((step, index) =>
            <ProcessStep data-component-id="src/app/services/components/ServicesInteractive.tsx:558:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="558" data-component-end-line="564" data-component-file="ServicesInteractive.tsx" data-component-name="ProcessStep" data-component-content="%7B%22elementName%22%3A%22ProcessStep%22%7D"
              key={step.number}
              number={step.number}
              title={step.title}
              description={step.description}
              icon={step.icon}
              isLast={index === processSteps.length - 1} />

            )}
          </div>
        </div>
      </section>

    </div>);

};

export default ServicesInteractive;